import React, {
	useCallback,
	useEffect,
	useState,
} from 'react';
import style from './ListingConfig.module.css';
import CustomIcon from '../Common/CustomIcon/CustomIcon.js';
import TextSmall from 'Vulture/Field/TextSmall.js';
import { useFlushableInput, } from 'Vulture/lib/FormFlush.js';
import Select from 'react-select';

import {
	buildTestData,
	removeWhitespace,
} from 'Vulture/TestData.js';

import {
	toggleAIGeneration,
	setAITriggerType,
	setAIPromptTemplate,
} from 'Vulture/AIGeneration.js';

import AIPrompt from './AIPrompt';

import {
	useListingConfig,
} from './ListingConfigContext.js';

const invalidListingColumnTypes = [ 'Concatenator', 'Building Picker', ];

// Column types whose data may be surfaced in the wayfinding info box; only
// these show the "Infobox Data" checkbox. Note 'Building Picker' is
// intentionally here AND in invalidListingColumnTypes above — it's ineligible
// for AI Population but eligible for Infobox Data (the two lists gate
// independent features).
const infoboxDataListingColumnTypes = [
	'Text Small',
	'Text Medium',
	'Text Large',
	'Image',
	'Primary Phone',
	'Phone',
	'Name',
	'Photo',
	'Address',
	'Building',
	'Logo',
	'Floor',
	'Location (apt/suite)',
	'Building Picker',
];

// Regex to match placeholders like {{field name}}
const placeholderPattern = /\{\{([^}]+)\}\}/g;

const ON_CREATE = 'onCreate';
const ON_UPDATE = 'onUpdate';

export const ListingColumnConfig = ({
	listingColumnUUID,
	index,
	app,
	listingConfigUUID,
	accountUUID,
	listingColumns,
	listingColumnTypeOptions,
	listingColumnTypeValue,
	listingColumnTypeUUID,
	deleteColumnButton,
	nameLocked,
	isTypeLocked,
	disableInputs,
	styles,
	arrayOptions,
}) => {
	const { updateListingColumn, updateListingColumnType, } = useListingConfig();

	const listingColumn = listingColumns.find((listingColumnData) => {
		return listingColumnData.uuid === listingColumnUUID;
	});

	const listingColumnName = listingColumn.display_name;
	const listingColumnTypeLabel = listingColumnTypeValue?.label || '';
	const canBeAIPopulated = !invalidListingColumnTypes.includes(listingColumnTypeLabel) && !listingColumn.pending;
	const canBeInfoboxData = infoboxDataListingColumnTypes.includes(listingColumnTypeLabel) && !listingColumn.pending;
	// Defaults off (DB default is 0); columns must be explicitly opted in.
	const infoboxDataOn = !!listingColumn?.infobox_data;

	const handleNameBlur = useCallback((value) => {
		updateListingColumn(listingConfigUUID, listingColumnUUID, { display_name: value, });
	}, [ updateListingColumn, listingConfigUUID, listingColumnUUID, ]);
	const nameInputRef = useFlushableInput(handleNameBlur);

	const initialAIConfig = listingColumn?.ai_generation_config;
	const [ aiToggle, setAIToggle, ] = useState(initialAIConfig?.is_active || false);
	const [ triggerType, setTriggerType, ] = useState(initialAIConfig?.trigger_type || ON_CREATE);

	useEffect(() => {
		setAIToggle(initialAIConfig?.is_active || false);
	}, [ initialAIConfig, ]);

	useEffect(() => {
		setTriggerType(initialAIConfig?.trigger_type || ON_CREATE);
	}, [ initialAIConfig, ]);

	const aiIsOnCreate = triggerType === ON_CREATE;
	const aiIsOnUpdate = triggerType === ON_UPDATE;

	const aiPrompt = initialAIConfig?.prompt_template || '';

	// Build available fields for autocomplete (excluding current column, invalid types, and AI-populated fields)
	const availableFieldsForAutocomplete = listingColumns
		.filter((lc) => {
			// Exclude the current column
			if (lc.uuid === listingColumnUUID) {
				return false;
			}
			// Pending inserts aren't on the server yet; AI prompts can't reference them.
			if (lc.pending) {
				return false;
			}
			// Exclude columns with AI generation enabled
			if (lc.ai_generation_config?.is_active) {
				return false;
			}
			// Check if it's a valid column type
			const isValidColumn = Object.entries(lc)
				.filter(([ key, ]) => {
					return key.startsWith('listing_column_type.');
				})
				.some(([ , value, ]) => {
					if (!value?.display_name) {
						return false;
					}
					return !invalidListingColumnTypes.includes(value.display_name);
				});
			return isValidColumn;
		})
		.map((lc) => {
			return {
				label: lc.display_name,
				value: lc.uuid,
			};
		});

	const aiPromptToDisplayText = aiToggle && (() => {
		let parsedAIPrompt = aiPrompt;

		const matches = [ ...aiPrompt.matchAll(placeholderPattern), ];
		matches.forEach((match) => {
			const matchedUUID = match[1];

			const lco = listingColumns.find((lc) => {
				if (lc.uuid !== matchedUUID) {
					return false;
				}

				const isValidColumn = Object.entries(lc)
					.filter(([ key, ]) => {
						return key.startsWith('listing_column_type.');
					})
					.filter(([ , value, ]) => {
						if (!value?.display_name) {
							return false;
						}

						return !invalidListingColumnTypes.includes(value.display_name);
					});
				return isValidColumn;
			});

			if (lco) {
				parsedAIPrompt = parsedAIPrompt.replace(matchedUUID, lco.display_name);
			}
		});

		return parsedAIPrompt;
	})();

	// Optimistic update handlers
	const handleToggleAI = async () => {
		const previousState = aiToggle;
		const nextState = !aiToggle;
		setAIToggle(nextState); // Optimistic update

		app.queuePendingPromises(`AIGenerationToggle_${listingColumnUUID}`, [
			async () => {
				try {
					await toggleAIGeneration(listingColumnUUID, nextState);
					console.log('Success: AI toggle updated');
				} catch (error) {
					console.error('Error:', error);
					setAIToggle(previousState); // Rollback on error
					alert('Failed to toggle AI generation. Please try again.');
				}
			},
		]);
	};

	const handleSetTriggerType = async (listingColumnUUID, newTriggerType) => {
		const previousState = triggerType;
		setTriggerType(newTriggerType); // Optimistic update

		app.queuePendingPromises(`AIGenerationTrigger_${listingColumnUUID}`, [
			async () => {
				try {
					await setAITriggerType(listingColumnUUID, newTriggerType);
					console.log('Success: Trigger type updated');
				} catch (error) {
					console.error('Error:', error);
					setTriggerType(previousState); // Rollback on error
					alert('Failed to update trigger type. Please try again.');
				}
			},
		]);
	};

	const handleSetPromptTemplate = async (listingColumnUUID, aiDisplayText) => {
		if (!aiDisplayText || removeWhitespace(aiDisplayText) === '') {
			return;
		}

		const aiDisplayTextToPrompt = (() => {
			let parsedAIDisplayText = aiDisplayText;

			const matches = [ ...aiDisplayText.matchAll(placeholderPattern), ];
			matches.forEach((match) => {
				const matchedDisplayText = match[1];

				const lco = listingColumns.find((lc) => {
					if (lc.display_name !== matchedDisplayText) {
						return false;
					}

					const isValidColumn = Object.entries(lc)
						.filter(([ key, ]) => {
							return key.startsWith('listing_column_type.');
						})
						.filter(([ , value, ]) => {
							if (!value?.display_name) {
								return false;
							}

							return !invalidListingColumnTypes.includes(value.display_name);
						});
					return isValidColumn;
				});

				if (lco) {
					parsedAIDisplayText = parsedAIDisplayText.replace(matchedDisplayText, lco.uuid);
				}
			});

			return parsedAIDisplayText;
		})();

		app.queuePendingPromises(`AIGenerationPrompt_${listingColumnUUID}`, [
			async () => {
				try {
					await setAIPromptTemplate(listingColumnUUID, aiDisplayTextToPrompt);
					console.log('Success: Prompt template updated');
				} catch (error) {
					console.error('Error:', error);
					alert('Failed to update prompt template. Please try again.');
				}
			},
		]);
	};

	// Build dataField based on column type
	let dataField = (
		<TextSmall
			defaultValue={listingColumnName}
			onBlurCB={handleNameBlur}
			inputRef={nameInputRef}
			dataTestingInfo={buildTestData(`input--Data${index + 1}`)}
			dataTestId={`listingColumnNameInput-${index + 1}`}
		/>
	);

	if (listingColumnTypeLabel === 'Array') {
		let options = [
			{
				label: '',
				value: '',
			},
		];

		if (arrayOptions && Object.keys(arrayOptions).length > 0) {
			options = Object.keys(arrayOptions).map((option) => {
				return {
					label: option,
					value: option,
				};
			});
		}

		dataField = (
			<Select
				className={style.listingConfigSelectLabel}
				options={options}
				styles={styles}
				onChange={(e) => {
					updateListingColumn(listingConfigUUID, listingColumnUUID, { display_name: e.value, });
				}}
				value={options.find((option) => {
					return option.value === listingColumnName;
				})}
			/>
		);
	} else if (listingColumnTypeLabel === 'Concatenator') {
		const listingColumnMetadataStr = listingColumn?.column_metadata || '{"fields": ["", ""], "separator": "-"}';
		const listingColumnMetadata = JSON.parse(listingColumnMetadataStr);

		const listingColumnMetadataFields = [];

		const columnOptions = listingColumns
			.filter((_listingColumn) => {
				// Prevents concatenating other concatenators
				if (_listingColumn?.['listing_column_type.string']?.display_name === 'Concatenator') {
					return false;
				}

				return _listingColumn?.['listing_column_type.string']?.type === 'string';
			})
			.map((_listingColumn) => {
				return {
					label: _listingColumn.display_name,
					value: _listingColumn.uuid,
				};
			});

		for (let j = 0; j <= listingColumnMetadata.fields.length; j++) {
			if (j === 0) {
				listingColumnMetadataFields.push(<>
					<div>Field 1</div>
					<div className={style.concatField}>
						<Select
							className={style.listingConfigSelectLabel}
							options={columnOptions}
							value={columnOptions.find((option) => {
								return option.value === listingColumnMetadata.fields[j];
							})}
							onChange={(e) => {
								listingColumnMetadata.fields[j] = e.value;
								updateListingColumn(listingConfigUUID, listingColumnUUID, {
									column_metadata: JSON.stringify(listingColumnMetadata),
								});
							}}
							styles={styles}
						/>
					</div>
				</>);
			} else if (j === 1) {
				listingColumnMetadataFields.push(<>
					<div>Separator</div>
					<div className={style.concatField}>
						<TextSmall
							defaultValue={listingColumnMetadata.separator}
							onBlurCB={(e) => {
								listingColumnMetadata.separator = e;
								updateListingColumn(listingConfigUUID, listingColumnUUID, {
									column_metadata: JSON.stringify(listingColumnMetadata),
								});
							}}
							dataTestingInfo={buildTestData(`input--concat-separator`)}
						/>
					</div>
				</>);
			} else {
				listingColumnMetadataFields.push(<>
					<div>{`Field ${j}`}</div>
					<div className={style.concatField}>
						<Select
							className={style.listingConfigSelectLabel}
							options={columnOptions}
							value={columnOptions.find((option) => {
								return option.value === listingColumnMetadata.fields[j - 1];
							})}
							onChange={(e) => {
								listingColumnMetadata.fields[j - 1] = e.value;
								updateListingColumn(listingConfigUUID, listingColumnUUID, {
									column_metadata: JSON.stringify(listingColumnMetadata),
								});
							}}
							styles={styles}
						/>
						{j === listingColumnMetadata.fields.length &&
							<div
								className={style.deleteIcon}
								onClick={() => {
									listingColumnMetadata.fields.push('');
									updateListingColumn(listingConfigUUID, listingColumnUUID, {
										column_metadata: JSON.stringify(listingColumnMetadata),
									});
								}}
							>
								<CustomIcon icon='plus-circle' />
							</div>
						}
						{j > 2 && <div
							className={style.deleteIcon}
							onClick={() => {
								listingColumnMetadata.fields.splice(j - 1, 1);
								updateListingColumn(listingConfigUUID, listingColumnUUID, {
									column_metadata: JSON.stringify(listingColumnMetadata),
								});
							}}
						>
							<CustomIcon icon='minus-circle' />
						</div>}
					</div>
				</>);
			}
		}

		dataField = (
			<div>
				<TextSmall
					defaultValue={listingColumnName}
					onBlurCB={handleNameBlur}
					inputRef={nameInputRef}
					dataTestingInfo={buildTestData(`input--Data${index + 1}`)}
					dataTestId={`listingColumnNameInput-${index + 1}`}
				/>
				{listingColumnMetadataFields}
			</div>
		);

	} else if (listingColumnTypeLabel === 'SMS Message' ||
		listingColumnTypeLabel === 'Email Message' ||
		listingColumnTypeLabel === 'Teams' ||
		listingColumnTypeLabel === 'Slack') {
		const messageLabels = app.messageLabels || [];
		let options = [
			{
				label: '',
				value: '',
			},
		];

		if (messageLabels) {
			options = messageLabels.map((option) => {
				return {
					label: option.label,
					value: option.label,
				};
			});
		}

		dataField = (
			<Select
				className={style.listingConfigSelectLabel}
				options={options}
				styles={styles}
				onChange={(e) => {
					updateListingColumn(listingConfigUUID, listingColumnUUID, { display_name: e.value, });
				}}
				value={options.find((option) => {
					return option.value === listingColumnName;
				})}
			/>
		);
	}

	// AI Generation rendering conditions
	const showAIToggle = canBeAIPopulated;
	const showAIConfiguration = canBeAIPopulated && aiToggle;

	return (
		<>
			<div
				className={style.listingConfigDataRow}
				key={listingColumnUUID}
			>
				<div className={style.listingConfigDataRowInput}>
					<div>Data {index + 1}</div>
					{dataField}
					{showAIToggle && <div className={style.checkbox}>
						<div
							className={aiToggle ? style.activeCheckbox : style.disabledSquare}
							onClick={(e) => {
								e.preventDefault();
								handleToggleAI(listingColumnUUID);
							}}>
							<div className={style.checkboxIcon}>
								{aiToggle && <CustomIcon icon="check"/>}
							</div>
							AI Population
							<div className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<div className={style.settingHelpToolTip} style={{
									zIndex: 1,
								}}>{'Turn on to enable AI powered auto generation on this column.'}</div>
							</div>
						</div>
					</div>}
					{canBeInfoboxData && <div className={style.checkbox}>
						<div
							className={infoboxDataOn ? style.activeCheckbox : style.disabledSquare}
							onClick={(e) => {
								e.preventDefault();
								updateListingColumn(listingConfigUUID, listingColumnUUID, { infobox_data: !infoboxDataOn, });
							}}>
							<div className={style.checkboxIcon}>
								{infoboxDataOn && <CustomIcon icon="check"/>}
							</div>
							Infobox Data
							<div className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<div className={style.settingHelpToolTip} style={{
									zIndex: 1,
								}}>{`Turn on to allow this column's data to show in the map info box.`}</div>
							</div>
						</div>
					</div>}
					{showAIConfiguration && <>
					</>}
				</div>

				<div className={style.listingConfigDataRowInput}>
					{<div>Type:</div>}
					<div className={style.listingConfigSubcontainer} data-test-id={`listingColumn-${listingColumnUUID}`}>
						<Select
							className={`${style.listingConfigSelectLabel} type-select-${index + 1}`}
							defaultValue={listingColumnTypeOptions[0]}
							isDisabled={nameLocked || isTypeLocked || disableInputs}
							options={listingColumnTypeOptions}
							styles={styles}
							onChange={(e) => {
								updateListingColumnType(
									listingConfigUUID,
									listingColumnUUID,
									listingColumnTypeUUID,
									e.value,
								);
							}}
							value={listingColumnTypeValue}
						/>
						{deleteColumnButton}
					</div>
				</div>
			</div>

			{showAIConfiguration && <>
				<div className={style.aiTriggerHeader}>
					<div className={style.checkbox}>
						<div className={style.mfaToggleContainer}>
							<div className={style.label}>On Create</div>
							<div className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<div className={style.settingHelpToolTip} style={{
									zIndex: 1,
								}}>{`Select 'On Create' to populate this column when a new listing is created.`}</div>
							</div>
						</div>
						<div className={style.activeToggle}>
							<input
								className={`on-off on-off-label`}
								type='checkbox'
								checked={aiIsOnCreate}
								onChange={() => {
									handleSetTriggerType(listingColumnUUID, ON_CREATE);
								}}
							/>
						</div>
					</div>
					<div className={style.checkbox}>
						<div className={style.mfaToggleContainer}>
							<div className={style.label}>On Update</div>
							<div className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<div className={style.settingHelpToolTip} style={{
									zIndex: 1,
								}}>{`Select 'On Update' to populate this column when a listing is created or edited.`}</div>
							</div>
						</div>
						<div className={style.activeToggle}>
							<input
								className={`on-off on-off-label`}
								type='checkbox'
								checked={aiIsOnUpdate}
								onChange={() => {
									handleSetTriggerType(listingColumnUUID, ON_UPDATE);
								}}
							/>
						</div>
					</div>
				</div>
				<div>
					<div className={style.aiPromptHeader}>
						AI Prompt
						<div className={style.settingHelpIcon}>
							<CustomIcon icon='question-circle' />
							<div className={style.settingHelpToolTip} style={{
								zIndex: 1,
							}}>{`Type in your prompt. You can type '{{' to use other columns as variables.`}</div>
						</div>
					</div>
					<AIPrompt
						defaultValue={aiPromptToDisplayText}
						listingColumnUUID={listingColumnUUID}
						setPrompt={handleSetPromptTemplate}
						dataTestingInfo={buildTestData(`input--ai_prompt${index + 1}`)}
						availableFields={availableFieldsForAutocomplete}
					/>
				</div>
			</>}
		</>
	);
};
